import { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoPlayerProps {
  streamId: string;
}

const detectBrowserCapabilities = () => {
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAnySafari = isSafari || isIOS;

  // Safari 17+ has ManagedMediaSource for VHS support
  const hasManagedMediaSource = typeof window !== 'undefined' &&
    'ManagedMediaSource' in window;

  return {
    isAnySafari,
    hasManagedMediaSource,
    canUseVHSOnSafari: isAnySafari && hasManagedMediaSource,
    useNativeHLS: isAnySafari && !hasManagedMediaSource
  };
};

const getVHSConfig = (capabilities: ReturnType<typeof detectBrowserCapabilities>) => {
  if (capabilities.isAnySafari) {
    // Always use native HLS on Safari - more reliable than VHS/MMS
    // Safari's native HLS support is excellent and avoids issues with
    // experimentalUseMMS (see https://github.com/videojs/video.js/issues/8940)
    return { overrideNative: false };
  }

  // Chrome/Firefox: standard VHS
  return {
    overrideNative: true,
    liveEdgeDelay: 10,
    liveSyncDurationCount: 3,
    maxPlaylistRetries: 10,
    smoothQualityChange: true,
    handlePartialData: true
  };
};

export default function VideoPlayer({ streamId }: VideoPlayerProps) {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const stallCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      videoElement.classList.add('vjs-fluid');
      videoRef.current?.appendChild(videoElement);

      // Get session token for HLS authentication
      const sessionToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('session_token='))
        ?.split('=')[1];

      // Use the stream ID to construct HLS URL
      // The actual stream key mapping happens on the server
      const hlsUrl = `/hls/${streamId}/index.m3u8${sessionToken ? `?token=${sessionToken}` : ''}`;

      const capabilities = detectBrowserCapabilities();
      const vhsConfig = getVHSConfig(capabilities);

      console.log('[VideoPlayer] Browser capabilities:', capabilities);
      console.log('[VideoPlayer] VHS config:', vhsConfig);

      playerRef.current = videojs(videoElement, {
        autoplay: 'muted',
        muted: true,
        controls: true,
        responsive: true,
        fluid: true,
        liveui: true,
        playsinline: true,
        html5: {
          vhs: {
            ...vhsConfig,
            withCredentials: true
          },
          nativeAudioTracks: capabilities.useNativeHLS,
          nativeVideoTracks: capabilities.useNativeHLS
        },
        sources: [{
          src: hlsUrl,
          type: 'application/x-mpegURL'
        }]
      });

      // Auto-unmute after playback starts (user can then control volume)
      playerRef.current.on('playing', () => {
        // Show unmute button prominently - player starts muted for autoplay
        console.log('[VideoPlayer] Playing - click to unmute');
      });

      // Error recovery with retry logic
      const MAX_RETRY_ATTEMPTS = 3;
      let retryCount = 0;

      playerRef.current.on('error', () => {
        const error = playerRef.current?.error();
        console.error('[VideoPlayer] Error:', error);

        const isRecoverableError = error?.code === 2 || error?.code === 4;

        if (isRecoverableError && retryCount < MAX_RETRY_ATTEMPTS) {
          retryCount++;
          console.log(`[VideoPlayer] Attempting recovery (${retryCount}/${MAX_RETRY_ATTEMPTS})...`);
          setTimeout(() => {
            playerRef.current?.src({ src: hlsUrl, type: 'application/x-mpegURL' });
            playerRef.current?.play().catch(() => {});
          }, 2000);
        }
      });

      // Reset retry count on successful playback
      playerRef.current.on('playing', () => {
        retryCount = 0;
      });

      // Native HLS stall recovery for older Safari
      if (capabilities.useNativeHLS) {
        let lastTime = 0;
        let stallCount = 0;

        stallCheckIntervalRef.current = setInterval(() => {
          const currentTime = playerRef.current?.currentTime() || 0;
          if (!playerRef.current?.paused() && currentTime === lastTime && currentTime > 0) {
            stallCount++;
            if (stallCount >= 3) {
              console.log('[VideoPlayer] Stall detected, attempting recovery...');
              playerRef.current?.currentTime(currentTime + 0.5);
              stallCount = 0;
            }
          } else {
            stallCount = 0;
          }
          lastTime = currentTime;
        }, 3000);
      }

      // Debug logging for live sync issues
      playerRef.current.on('waiting', () => {
        console.log('[VideoPlayer] Buffering...');
      });

      playerRef.current.on('playing', () => {
        console.log('[VideoPlayer] Playing');
      });
    }

    return () => {
      if (stallCheckIntervalRef.current) {
        clearInterval(stallCheckIntervalRef.current);
        stallCheckIntervalRef.current = null;
      }
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [streamId]);

  return (
    <div data-vjs-player style={{ width: '100%', height: '100%' }}>
      <div ref={videoRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
